import createDebug from 'debug';

const debug = createDebug('app:api');
const API_BASE = '/api';

// ============================================
// Bearer Token Authentication
// ============================================

const JWT_STORAGE_KEY = 'links-auth-token';

/**
 * Get JWT from localStorage
 */
export function getAuthToken(): string | null {
	return localStorage.getItem(JWT_STORAGE_KEY);
}

/**
 * Store JWT in localStorage
 */
export function setAuthToken(token: string): void {
	localStorage.setItem(JWT_STORAGE_KEY, token);
}

/**
 * Clear JWT from localStorage
 */
export function clearAuthToken(): void {
	localStorage.removeItem(JWT_STORAGE_KEY);
}

/**
 * Create headers object with Authorization Bearer token
 */
export function getAuthHeaders(contentType?: string): Record<string, string> {
	const headers: Record<string, string> = {};
	const token = getAuthToken();

	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	if (contentType) {
		headers['Content-Type'] = contentType;
	}

	return headers;
}

/**
 * Make an authenticated fetch request with Bearer token
 * Automatically handles 401 responses by clearing token and redirecting to login
 */
export async function authFetch(url: string, options: RequestInit = {}): Promise<Response> {
	const token = getAuthToken();
	const headers = new Headers(options.headers);

	if (token) {
		headers.set('Authorization', `Bearer ${token}`);
	}

	const response = await fetch(url, {
		...options,
		headers,
	});

	// Handle 401 Unauthorized - token is invalid or expired
	if (response.status === 401) {
		debug('authFetch: 401 Unauthorized - clearing token and redirecting to login');
		clearAuthToken();
		// Redirect to login page
		window.location.href = '/login';
		throw new Error('Unauthorized - please log in again');
	}

	return response;
}

/**
 * Generic API request helper with JSON parsing.
 * Wraps authFetch and handles response/error parsing.
 */
export async function apiRequest<T>(
	endpoint: string,
	options: RequestInit = {}
): Promise<T> {
	const url = endpoint.startsWith('/') ? `${API_BASE}${endpoint}` : endpoint;

	const response = await authFetch(url, {
		...options,
		headers: {
			'Content-Type': 'application/json',
			...options.headers,
		},
	});

	if (!response.ok) {
		const errorText = await response.text();
		let errorMessage: string;
		try {
			const errorJson = JSON.parse(errorText);
			errorMessage = errorJson.error || errorJson.message || errorText;
		} catch {
			errorMessage = errorText || `HTTP ${response.status}`;
		}
		throw new Error(errorMessage);
	}

	return response.json();
}

export interface TagResponse {
	id: number;
	name: string;
	color: string | null;
	icon: string | null;
	isNsfw?: boolean;
	autoDownload?: boolean;
	linkCount?: number;
	isPersonal?: boolean;
	createdAt?: string;
}

export interface LinkResponse {
	id: number;
	url: string;
	title: string;
	description: string | null;
	queueId: number;
	metadata: Record<string, unknown> | null;
	source: string | null;
	thumbnailUrl: string | null;
	isEnriching: boolean;
	createdAt: string;
	updatedAt: string;
	archivedAt: string | null;
	snoozedUntil?: string | null;
	tags?: TagResponse[];
}

// Slim response for list views (excludes metadata, source, createdAt, archivedAt)
export interface LinkSummaryResponse {
	id: number;
	url: string;
	title: string;
	description: string | null;
	queueId: number;
	thumbnailUrl: string | null;
	isEnriching: boolean;
	updatedAt: string;
	snoozedUntil?: string | null;
	tags?: TagResponse[];
}

export interface LinksListResponse {
	links: LinkResponse[];
	total: number;
	limit: number;
	offset: number;
}

// Per-queue response (uses slim links)
export interface QueueLinksResponse {
	links: LinkSummaryResponse[];
	total: number;
	queueId: number;
}

export interface QueueResponse {
	id: number;
	slug: string;
	name: string;
	color: string;
	icon: string | null;
	position: number;
	linkCount?: number;
	snoozedCount?: number;
}

export interface QueuesListResponse {
	queues: QueueResponse[];
}

export interface CategoryResponse {
	id: string;
	name: string;
	color: string;
	icon?: string;
	nsfw: boolean;
	linkCount?: number;
}

// ============================================
// Query functions migrated to GraphQL - see useGraphQLQueries.ts
// ============================================
// fetchLinks -> fetchQueueLinksGQL, fetchArchivedLinksGQL
// fetchQueueLinks -> fetchQueueLinksGQL
// fetchLinkDetails -> fetchLinkDetailsGQL
// fetchQueues -> useQueuesQuery
// fetchCategories -> useCategoriesQuery
// searchLinks -> searchLinksGQL
// fetchTags -> useTagsQuery
// fetchRelatedLinks -> useLinkWithRelatedQuery

// Link/search/enrich mutations moved to GraphQL - see useGraphQLMutations.ts

// ============================================
// Quick Add / Link Submission API
// ============================================

// SubmitLink mutation moved to GraphQL - see useGraphQLMutations.ts

// ============================================
// Batch Operations API (types only - functions moved to GraphQL)
// ============================================

export interface BatchOperationsInput {
	enrich?: number[];
	move?: { queueId: number; ids: number[] };
	categorize?: { category: string; ids: number[] };
	archive?: number[];
	unarchive?: number[];
}

export interface BatchOperationsResponse {
	results: {
		enrich?: { queued: number };
		move?: { updated: number };
		categorize?: { updated: number };
		archive?: { updated: number };
		unarchive?: { updated: number };
	};
	errors?: string[];
}

// batchOperations function moved to GraphQL - see useGraphQLMutations.ts

// ============================================
// Bulk Enrich API (types only - function moved to GraphQL)
// ============================================

export interface BulkEnrichInput {
	linkIds: number[];
	enrichers?: string[];
	force?: boolean;
}

export interface BulkEnrichResponse {
	queued: number;
	enricherJobsCreated: number;
	enrichers: string[];
	force: boolean;
}

// bulkEnrich function moved to GraphQL - see useGraphQLMutations.ts

// ============================================
// Status API (types only - functions moved to GraphQL)
// ============================================

export interface SystemStatusResponse {
	links: {
		total: number;
		enrichmentPending: number;
		archived: number;
		byQueue: Record<string, number>;
		byCategory: Record<string, number>;
	};
	cache: {
		thumbnails: {
			count: number;
			sizeBytes: number;
		};
	};
}

export interface EnrichmentStatusResponse {
	pending: number;
	total: number;
}

// ============================================
// Related Links Types (functions moved to GraphQL)
// ============================================

export interface RelatedLink {
	id: number;
	url: string;
	title: string;
	thumbnailUrl: string | null;
	archivedAt: string | null;
	matchingTags: string[];
	createdAt: string;
}

export interface RelatedLinksResponse {
	relatedLinks: RelatedLink[];
	sourceLink: { id: number; userTags: string[] };
	total: number;
}

// ============================================
// API Key Management
// ============================================

export interface ApiKeyInfo {
	id: number;
	keyPrefix: string;
	name: string;
	createdAt: string;
	lastUsedAt: string | null;
	expiresAt: string | null;
	scopes: string[] | null; // Custom scopes (null = inherit all user permissions)
	isActive: boolean;
}

export interface CreateApiKeyResponse {
	fullKey: string;
	keyInfo: ApiKeyInfo;
	warning: string;
}

export interface ListApiKeysResponse {
	keys: ApiKeyInfo[];
}

export async function listApiKeys(): Promise<ListApiKeysResponse> {
	const response = await authFetch(`${API_BASE}/auth/api-keys`);
	if (!response.ok) throw new Error('Failed to list API keys');
	return response.json();
}

export async function createApiKey(input: {
	name: string;
	expiresAt?: number;
	scopes?: string[]; // Custom scopes (omit = inherit all user permissions)
}): Promise<CreateApiKeyResponse> {
	debug('createApiKey: name=%s scopes=%o', input.name, input.scopes);
	const response = await authFetch(`${API_BASE}/auth/api-keys`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(input),
	});
	if (!response.ok) {
		const errorData = await response.json();
		throw new Error(errorData.error || 'Failed to create API key');
	}
	return response.json();
}

export async function deleteApiKey(keyId: number): Promise<{ success: boolean; message: string }> {
	debug('deleteApiKey: keyId=%d', keyId);
	const response = await authFetch(`${API_BASE}/auth/api-keys/${keyId}`, {
		method: 'DELETE',
	});
	if (!response.ok) {
		const errorData = await response.json();
		throw new Error(errorData.error || 'Failed to delete API key');
	}
	return response.json();
}

export interface UpdateApiKeyScopesResponse {
	success: boolean;
	keyInfo: {
		id: number;
		keyPrefix: string;
		name: string;
		scopes: string[] | null;
	} | null;
}

export async function updateApiKeyScopes(
	keyId: number,
	scopes: string[] | null
): Promise<UpdateApiKeyScopesResponse> {
	debug('updateApiKeyScopes: keyId=%d scopes=%o', keyId, scopes);
	const response = await authFetch(`${API_BASE}/auth/api-keys/${keyId}`, {
		method: 'PATCH',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ scopes }),
	});
	if (!response.ok) {
		const errorData = await response.json();
		throw new Error(errorData.error || 'Failed to update API key scopes');
	}
	return response.json();
}
